import React, { useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { Button } from './ui/Button';
import { Dialog } from './ui/Dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SystemAPI } from '../lib/api';
import { useToast } from './ui/Toast';
import { formatBytes } from '../lib/format';

interface Service {
  name: string;
  activeState: string;
  subState: string;
  memoryBytes?: number;
  nRestarts?: number;
  activeEnterTimestamp?: string;
}

interface ServiceCardProps {
  service: Service;
}

export function ServiceCard({ service }: ServiceCardProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { toast } = useToast();
  const qc = useQueryClient();

  const isActive = service.activeState === 'active';

  const restartMut = useMutation({
    mutationFn: () => SystemAPI.restartService(service.name),
    onSuccess: () => {
      toast(`Сервіс ${service.name} перезапущено`, 'success');
      qc.invalidateQueries({ queryKey: ['services'] });
      setConfirmOpen(false);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  return (
    <>
      <div
        className="flex items-center justify-between py-2 border-b border-tg-border last:border-0"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-base">{isActive ? '🟢' : '🔴'}</span>
          <div className="min-w-0">
            <div className="text-sm font-medium text-tg-text truncate">{service.name}</div>
            <div className="text-xs text-tg-text-secondary">
              {service.subState}
              {service.memoryBytes ? ` · ${formatBytes(service.memoryBytes)}` : ''}
              {service.nRestarts != null ? ` · ${service.nRestarts} рестартів` : ''}
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setConfirmOpen(true)}
          title="Перезапустити"
        >
          <RotateCcw size={14} />
        </Button>
      </div>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Перезапустити сервіс?"
      >
        <p className="text-sm text-tg-text-secondary mb-4">
          Сервіс <strong className="text-tg-text">{service.name}</strong> буде перезапущено.
        </p>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => setConfirmOpen(false)}>
            Скасувати
          </Button>
          <Button
            variant="danger"
            loading={restartMut.isPending}
            onClick={() => restartMut.mutate()}
          >
            Перезапустити
          </Button>
        </div>
      </Dialog>
    </>
  );
}
